import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import {
  CANCEL_PAYMENT_MOCK,
  GET_ORDER_DETAIL_MOCK,
  GET_ORDER_STATUS_COUNTS_MOCK,
  GET_ORDERS_MOCK,
  INIT_PAYMENT_MOCKS,
  ORDER_MOCKS,
  PAYMENT_STATUS_MOCK,
  UPDATE_ORDER_STATUS_MOCK,
  VALIDATE_CART_MOCK,
} from '@shared/mocks/orders.mock';

import {
  CreateOrderRequest,
  CreateOrderResponse,
  InitPaymentResponse,
  PaymentStatusResponse,
  ValidateCartRequest,
  ValidateCartResponse,
} from './interfaces/order.interfaces';
import {
  Order,
  OrderListFilters,
  OrderStatusCounts,
  OrderSummary,
  PaginatedOrders,
  UpdateOrderStatusDto,
} from './interfaces/orders.interfaces';

import { HttpInterceptor } from '@interceptors/http/http.interceptor';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 12;

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly http = inject(HttpInterceptor);
  private readonly apiUrl = 'orders';

  /**
   * @param idempotencyKey Mismo valor en el intento original y en sus reintentos:
   * es lo que le permitiría al backend reconocer que se trata del mismo pedido.
   *
   * TODO(idempotencia): el backend todavía no lee esta cabecera. Mientras no la
   * lea, un reintento después de un timeout o un 5xx puede duplicar la orden,
   * por eso el checkout pide confirmación explícita antes de reintentar en ese
   * caso (ver `openUnconfirmedOrderModal`).
   */
  public createOrder(dto: CreateOrderRequest, idempotencyKey?: string): Observable<CreateOrderResponse> {
    const headers = idempotencyKey
      ? new HttpHeaders({ 'X-Idempotency-Key': idempotencyKey })
      : undefined;

    return this.http.post(this.apiUrl, dto, ORDER_MOCKS, headers);
  }

  /**
   * Revalida el carrito contra el catálogo antes de mostrar el checkout: los
   * precios del localStorage pueden estar viejos o haber sido manipulados.
   */
  public validateCart(dto: ValidateCartRequest): Observable<ValidateCartResponse> {
    return this.http.post(`${this.apiUrl}/cart/validate`, dto, VALIDATE_CART_MOCK);
  }

  public initPayment(orderId: string): Observable<InitPaymentResponse> {
    return this.http.post(`${this.apiUrl}/${orderId}/payment/init`, {}, INIT_PAYMENT_MOCKS);
  }

  public getPaymentStatus(orderId: string): Observable<PaymentStatusResponse> {
    return this.http.get(`${this.apiUrl}/${orderId}/payment/status`, PAYMENT_STATUS_MOCK);
  }

  /** Only moves a still pending payment to `rejected`; a resolved one comes back unchanged. */
  public cancelPayment(orderId: string): Observable<PaymentStatusResponse> {
    return this.http.post(`${this.apiUrl}/${orderId}/payment/cancel`, {}, CANCEL_PAYMENT_MOCK);
  }

  public getOrdersAdmin(
    page = DEFAULT_PAGE,
    limit = DEFAULT_LIMIT,
    filters: OrderListFilters = {},
  ): Observable<PaginatedOrders> {
    return this.http
      .get(this.apiUrl, GET_ORDERS_MOCK, undefined, { page, limit, ...this.cleanFilters(filters) })
      .pipe(map((response) => this.toPaginated(response, page, limit)));
  }

  /** Totales por estado de las pastillas: no dependen de la página, solo de la búsqueda. */
  public getOrderStatusCounts(filters: Omit<OrderListFilters, 'status'> = {}): Observable<OrderStatusCounts> {
    return this.http.get(
      `${this.apiUrl}/status-counts`,
      GET_ORDER_STATUS_COUNTS_MOCK,
      undefined,
      this.cleanFilters(filters),
    );
  }

  public getOrderById(id: string): Observable<Order> {
    return this.http.get(`${this.apiUrl}/${id}`, GET_ORDER_DETAIL_MOCK);
  }

  /** El endpoint pagina las filas; algunos mocks todavía devuelven el arreglo plano. */
  private toPaginated(response: OrderSummary[] | PaginatedOrders, page: number, limit: number): PaginatedOrders {
    if (!Array.isArray(response)) {
      return response;
    }

    return { data: response, total: response.length, page, limit, totalPages: 1 };
  }

  /** Los filtros vacíos no viajan: el back valida el valor de los que llegan. */
  private cleanFilters(filters: OrderListFilters): Record<string, string> {
    return Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
    ) as Record<string, string>;
  }

  /** El endpoint responde 204 No Content: no hay cuerpo que leer. */
  public updateOrderStatus(id: string, dto: UpdateOrderStatusDto): Observable<void> {
    return this.http.patch(`${this.apiUrl}/${id}/status`, dto, UPDATE_ORDER_STATUS_MOCK);
  }
}
